import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { queryKeys } from './queryClient';
import type { Notification, UnreadCount } from '../types';

const fetchNotifications = async (take: number = 50): Promise<Notification[]> => {
  return api.get<Notification[]>(`/notifications?take=${take}`);
};

const fetchUnreadCount = async (): Promise<UnreadCount> => {
  return api.get<UnreadCount>('/notifications/unread-count');
};

const markAsRead = async (id: string): Promise<void> => {
  return api.put<void>(`/notifications/${id}/read`);
};

const markAllAsRead = async (): Promise<void> => {
  return api.put<void>('/notifications/read-all');
};

export const useNotifications = (take: number = 50) => {
  return useQuery({
    queryKey: queryKeys.notifications.list(take),
    queryFn: () => fetchNotifications(take),
    staleTime: 1000 * 60, // 1 minute
  });
};

export const useUnreadCount = () => {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: fetchUnreadCount,
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60, // poll every 60 seconds
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
};

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
};
